<script lang="ts">
	import { onMount } from 'svelte';
	import { appStore } from '../stores/appStore';
	import { getUserActivities, getFollowingActivities, type Activity } from '../utils/activity';

	let profile: any = $state(null);
	let activities = $state<Activity[]>([]);
	let loading = $state(true);
	let error = $state('');
	let activeTab = $state<'all' | 'following'>('all');
	let loadingMore = $state(false);
	let hasMore = $state(true);
	let currentLimit = $state(20);

	// Subscribe to store
	appStore.subscribe(state => {
		profile = state.profile;
	});

	onMount(async () => {
		await loadActivities();
	});

	async function loadActivities() {
		try {
			loading = true;
			error = '';

			if (activeTab === 'all') {
				activities = await getUserActivities(currentLimit);
			} else {
				activities = await getFollowingActivities(currentLimit);
			}

			hasMore = activities.length >= currentLimit;
		} catch (e: any) {
			error = e.message || 'Failed to load activities';
			console.error('Error loading activities:', e);
		} finally {
			loading = false;
		}
	}

	async function loadMore() {
		if (loadingMore || !hasMore) return;

		try {
			loadingMore = true;
			currentLimit += 20;

			const newActivities = activeTab === 'all'
				? await getUserActivities(currentLimit)
				: await getFollowingActivities(currentLimit);

			activities = newActivities;
			hasMore = newActivities.length >= currentLimit;
		} catch (e: any) {
			console.error('Error loading more activities:', e);
		} finally {
			loadingMore = false;
		}
	}

	async function switchTab(tab: 'all' | 'following') {
		if (tab === activeTab) return;
		activeTab = tab;
		currentLimit = 20;
		await loadActivities();
	}

	function getStatusColor(status?: string) {
		if (!status) return '';
		return status === 'success'
			? 'text-green-600 dark:text-green-400'
			: status === 'failed'
			? 'text-red-600 dark:text-red-400'
			: '';
	}

	function getActivityTypeLabel(type: string): string {
		const labels: Record<string, string> = {
			'repo_created': 'Repository',
			'repo_forked': 'Fork',
			'repo_starred': 'Star',
			'repo_watched': 'Watch',
			'pr_opened': 'Pull Request',
			'pr_merged': 'Merge',
			'pr_closed': 'Pull Request',
			'issue_opened': 'Issue',
			'issue_closed': 'Issue',
			'issue_commented': 'Comment',
			'commit': 'Commit',
			'deployment': 'Deployment',
			'deployment_success': 'Deployment',
			'deployment_failed': 'Deployment',
			'database_created': 'Database'
		};
		return labels[type] || 'Activity';
	}
</script>

<div class="min-h-screen bg-gray-50 dark:bg-gray-900">
	<!-- Header -->
	<div class="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
		<div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
			<div class="flex items-center justify-between">
				<div class="flex items-center gap-6">
					<div>
						<h1 class="text-3xl font-bold text-gray-900 dark:text-white">Activity Feed</h1>
						<p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
							See what's happening across your repositories
						</p>
					</div>
					{#if !loading && !error && activities.length > 0}
						<div class="flex items-center gap-4 pl-6 border-l border-gray-200 dark:border-gray-700">
							<div class="text-center">
								<p class="text-lg font-semibold text-gray-900 dark:text-white">{activities.length}</p>
								<p class="text-xs text-gray-500 dark:text-gray-400">Activities</p>
							</div>
							<div class="text-center">
								<p class="text-lg font-semibold text-gray-900 dark:text-white">
									{new Set(activities.map(a => a.repo).filter(r => r)).size}
								</p>
								<p class="text-xs text-gray-500 dark:text-gray-400">Repos</p>
							</div>
							<div class="text-center">
								<p class="text-lg font-semibold text-gray-900 dark:text-white">
									{new Set(activities.map(a => a.type)).size}
								</p>
								<p class="text-xs text-gray-500 dark:text-gray-400">Types</p>
							</div>
						</div>
					{/if}
				</div>
				<a
					href="#/"
					class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
				>
					← Back to Dashboard
				</a>
			</div>

			<!-- Tabs -->
			<div class="mt-6 border-b border-gray-200 dark:border-gray-700">
				<nav class="-mb-px flex space-x-8">
					<button
						onclick={() => switchTab('all')}
						class="border-b-2 py-4 px-1 text-sm font-medium transition-colors {activeTab === 'all'
							? 'border-blue-500 text-blue-600 dark:text-blue-400'
							: 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'}"
					>
						All Activity
					</button>
					<button
						onclick={() => switchTab('following')}
						class="border-b-2 py-4 px-1 text-sm font-medium transition-colors {activeTab === 'following'
							? 'border-blue-500 text-blue-600 dark:text-blue-400'
							: 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'}"
					>
						Following
					</button>
				</nav>
			</div>
		</div>
	</div>

	<!-- Content -->
	<div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
		{#if loading}
			<!-- Loading State -->
			<div class="flex items-center justify-center py-12">
				<div class="text-center">
					<div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
					<p class="text-gray-600 dark:text-gray-400">Loading activities...</p>
				</div>
			</div>
		{:else if error}
			<!-- Error State -->
			<div class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
				<div class="flex">
					<div class="flex-shrink-0">
						<svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
							<path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
						</svg>
					</div>
					<div class="ml-3">
						<h3 class="text-sm font-medium text-red-800 dark:text-red-200">Error loading activities</h3>
						<p class="mt-1 text-sm text-red-700 dark:text-red-300">{error}</p>
					</div>
				</div>
			</div>
		{:else if activities.length === 0}
			<!-- Empty State -->
			<div class="text-center py-12">
				<svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
				</svg>
				<h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No activities yet</h3>
				<p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
					{#if activeTab === 'following'}
						Activities from people you follow will appear here.
					{:else}
						Start by creating a repository or starring some projects!
					{/if}
				</p>
				<div class="mt-6">
					<a
						href="#/repositories/new"
						class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
					>
						<svg class="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
						</svg>
						Create Repository
					</a>
				</div>
			</div>
		{:else}
			<!-- Activities List -->
			<div class="space-y-4">
				{#each activities as activity, index (activity.id)}
					<div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow">
						<div class="p-6">
							<div class="flex items-start space-x-4">
								<!-- Icon -->
								<div class="flex-shrink-0">
									<div class="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-2xl">
										{activity.icon}
									</div>
								</div>

								<!-- Content -->
								<div class="flex-1 min-w-0">
									<div class="flex items-start justify-between">
										<div class="flex-1">
											<!-- User and Message -->
											<p class="text-sm text-gray-900 dark:text-gray-100">
												<a
													href="#/users/{activity.userId}"
													class="font-semibold hover:text-blue-600 dark:hover:text-blue-400"
												>
													{activity.user}
												</a>
												<span class="ml-1 text-gray-600 dark:text-gray-400">{activity.message}</span>
											</p>

											<!-- Repository Link -->
											{#if activity.repo}
												<div class="mt-2 flex items-center space-x-3">
													<a
														href="#/users/{profile?.userName}/repositories/{activity.repo}"
														class="inline-flex items-center text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
													>
														<svg class="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
															<path fill-rule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clip-rule="evenodd" />
														</svg>
														{activity.repo}
													</a>

													{#if activity.status}
														<span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium {getStatusColor(activity.status)}">
															<span class="w-1.5 h-1.5 mr-1.5 rounded-full bg-current"></span>
															{activity.status}
														</span>
													{/if}
												</div>
											{/if}

											<!-- Time and Type Badge -->
											<div class="mt-2 flex items-center space-x-3 text-xs text-gray-500 dark:text-gray-400">
												<span>{activity.time}</span>
												<span>•</span>
												<span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
													{getActivityTypeLabel(activity.type)}
												</span>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				{/each}
			</div>

			<!-- Load More Button -->
			{#if hasMore}
				<div class="mt-8 text-center">
					<button
						onclick={loadMore}
						disabled={loadingMore}
						class="inline-flex items-center px-6 py-3 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
					>
						{#if loadingMore}
							<div class="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900 dark:border-gray-100 mr-2"></div>
							Loading...
						{:else}
							Load More Activities
						{/if}
					</button>
				</div>
			{/if}
		{/if}
	</div>
</div>
